import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../components/pageList";
import { IconTrash, IconPencil, IconSettings, IconCopy } from "@tabler/icons";
import {
  TextInput,
  NumberInput,
  Select,
  Checkbox,
  Text,
  Button,
  Paper,
} from "@mantine/core";

import ActionCellRender from "../components/actionCellRender";
import moment from "moment";
import UniqueTextInput from "../components/uniqueTextInput";
import CompensationPipeline from "../components/compensationPipeline";
import CompensationDataSource from "../components/compensationDataSource";
import CompensationMetrics from "../components/compensationMetrics";
import CompensationReward from "../components/compensationReward";
import CompensationResult from "../components/compensationResult";

import { DatePicker } from "@mantine/dates";
import ReactJson from "react-json-view";
import _ from "lodash";

const tableSchema = {
  columns: [
    {
      field: "name",
      headerName: "Campaign",
      cellRender: (col, data) => {
        return (
          <div>
            {data.name}
            {data.cname && (
              <Text size="xs" color="dimmed" ml={6} component="span">
                {data.cname}
              </Text>
            )}
          </div>
        );
      },
    },

    {
      field: "period",
      headerName: "Period",
      cellRender: (col, data) => {
        return (
          <div>
            {data.effectiveDate &&
              moment(data.effectiveDate).format("YYYY-MM-DD")}

            {data.expiryDate &&
              ` To ${moment(data.expiryDate).format("YYYY-MM-DD")}`}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
    },
    {
      field: "createdAt",
      headerName: "Created At",
      cellRender: (col, data) => {
        return <div>{moment(data.createdAt).format("YYYY-MM-DD HH:mm")}</div>;
      },
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      cellRender: (col, data) => {
        return <div>{moment(data.updatedAt).format("YYYY-MM-DD HH:mm")}</div>;
      },
    },
    {
      field: "action",
      headerName: "Action",
      cellRender: ActionCellRender,
      isAction: true,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
          label: "Edit",
        },
      ],
      actionMenu: [
        {
          name: "copy",
          label: "Copy",
          icon: IconCopy,
          props: {
            // color: "red",
          },
        },
        {
          name: "delete",
          label: "Delete data",
          icon: IconTrash,
          props: {
            color: "red",
          },
        },
      ],
    },
  ],
};

const formSchema = {
  display: {
    mode: ENUM_FORM_DISPLAY.LINK,
  },
  title: "Compensation",
  titleField: "code",
  apiEntity: "compensation",
  initialValues: {
    name: "",
  },

  layout: {
    containers: [
      {
        key: "main",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.TABS,
        props: {
          variant: "default", //default | pills | outline
          defaultValue: "Setting",
        },
        tab: [
          {
            key: "tab-setting",
            label: "Setting",
          },
          {
            key: "tab-result",
            label: "Result",
          },
          {
            key: "tab-logs",
            label: "Logs",
          },
        ],
      },
      //two columns grid
      {
        key: "section-setting",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Basic Setting",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      // section scope
      {
        key: "section-scope",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Scope",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-setting-g",
        parent: "section-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          {
            key: "section-setting-g-1",
            props: {
              span: 6,
            },
          },
          {
            key: "section-setting-g-2",
            props: {
              span: 6,
            },
          },
        ],
      },
      {
        key: "section-datasource",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Data Sources",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-metrics",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Metrics",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },

      {
        key: "section-rewards",
        parent: "tab-setting",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Rewards",
          withBorder: false,
        },
      },

      {
        key: "section-result",
        parent: "tab-result",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Result",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
      {
        key: "section-logs",
        parent: "tab-logs",
        type: ENUM_FORM_LAYOUT_CONTAINER.SECTION,
        props: {
          title: "Logs",
          withBorder: false,
          mt: 0,
          mb: "md",
        },
      },
    ],

    fields: [
      {
        name: "code",
        component: UniqueTextInput,
        parent: "section-setting-g-1",
        props: {
          required: true,
          uniqueEntity: "compensation",
          errorMessage: "Compensation is already exist.",
          label: "Compensation Number (Auto)",
          placeholder: "COM20250101",
          mt: 0,
        },
      },
      {
        name: "status",
        component: Select,
        parent: "section-setting-g-2",
        props: {
          label: "Status",
          required: true,
          data: ["ACTIVE", "INACTIVE", "EXPIRED"],
          searchable: true,
          defaultValue: "ACTIVE",
        },
      },
      {
        name: "name",
        component: TextInput,
        parent: "section-setting-g-1",
        props: {
          label: "Name",
          required: true,
          autoComplete: "off",
        },
      },
      //cname
      {
        name: "cname",
        component: TextInput,
        parent: "section-setting-g-2",
        props: {
          label: "Chinese Name",
          autoComplete: "off",
        },
      },
      //effective date
      {
        name: "effectiveDate",
        component: DatePicker,
        parent: "section-setting-g-1",
        type: "datePicker",
        props: {
          label: "Effective Date",
          required: true,
          allowFreeInput: true,
          placeholder: "YYYY-MM-DD",
          clearable: true,
          zIndex: 1000,
        },
      },
      //expiry date
      {
        name: "expiryDate",
        component: DatePicker,
        parent: "section-setting-g-2",
        type: "datePicker",
        props: {
          label: "Expiry Date",
          allowFreeInput: true,
          placeholder: "YYYY-MM-DD",
          clearable: true,
        },
      },

      //resursive select , options :["NONE", "MONTHLY", "QUARTERLY", "HALF_YEARLY", "YEARLY"]
      {
        name: "recursive",
        component: Select,
        parent: "section-setting-g-1",
        props: {
          label: "Recursive",
          required: true,
          data: ["NONE", "MONTHLY", "QUARTERLY", "HALF_YEARLY", "YEARLY"],
          searchable: true,
          defaultValue: "NONE",
        },
      },
      {
        name: "scope",
        component: Select,
        parent: "section-scope",
        props: {
          label: "Source of Compensation",
          required: true,
          data: ["AGENT", "MY TEAM", "WHOLE TEAM"],
          searchable: true,
          defaultValue: "AGENT",
        },
      },
      {
        //team level
        name: "teamLevel",
        component: NumberInput,
        parent: "section-scope",
        visibleOnly: (values, accessRight) => values.scope == "MY TEAM",
        props: {
          label: "Team Level",
          required: true,
          min: 1,
          max: 10,
          step: 1,
          defaultValue: 2,
        },
      },
      {
        name: "datasource",
        component: CompensationDataSource,
        parent: "section-datasource",
        props: {
          label: "Data Source",
        },
      },
      {
        name: "metrics",
        component: CompensationMetrics,
        parent: "section-metrics",
        props: {
          label: "Metrics",
        },
      },
      {
        name: "reward",
        component: CompensationReward,
        parent: "section-rewards",
        props: {
          label: "Reward",
        },
      },
      {
        name: "result",
        component: CompensationResult,
        parent: "section-result",
        props: {
          label: "Result",
        },
      },
    ],
  },
};

const CompensationSetting = () => {
  return (
    <PageList
      title={"Compensation Campaign"}
      apiEntity={"compensation"}
      tableSchema={tableSchema}
      formSchema={formSchema}
    />
  );
};

export default CompensationSetting;
